/**
 * Toast-шина приложения.
 *
 * История: до 2026-08-18 это была заглушка «до Phase 5B» — `toast.success`
 * писал в `console.log` (пользователь не видел НИЧЕГО), а `toast.error` звал
 * `window.alert` (модальный диалог ОС: фокус улетает, страница замирает,
 * несколько ошибок выстраиваются в очередь диалогов). За это время заглушка
 * разъехалась на ~280 вызовов в 70 файлах — design review 2026-08-18 (зона 2)
 * поставил это находкой №1 по «усталости».
 *
 * Теперь здесь честный неблокирующий тостер: store + `<Toaster/>`
 * (`components/ui/toaster.tsx`), смонтированный один раз в `App`. **Публичный
 * API `toast.success/error/warning/info(msg)` НЕ изменился** — все существующие
 * вызовы работают как есть, ничего мигрировать не надо.
 *
 * Правила поведения:
 *   - success / info — автоскрытие (успех не должен требовать клика);
 *   - error / warning — висят до закрытия пользователем (ошибку нельзя
 *     «промигать»), но НЕ блокируют работу со страницей;
 *   - повтор того же текста не множит стопку — второй одинаковый тост
 *     игнорируется, пока первый на экране;
 *   - при переполнении вытесняются сначала самоскрывающиеся уведомления —
 *     непрочитанная ошибка не выбивается пачкой «сохранено»;
 *   - если `<Toaster/>` ещё не смонтирован (ранний bootstrap: 401 первого же
 *     запроса) — сообщение уходит в консоль И придерживается в буфере до
 *     первого подписчика, а не теряется;
 *   - **итог массового действия** (`toastBulkResult`) — по центру экрана, а не
 *     в стопке снизу справа (AK-721): «заполнено полей 22 · 2 407 га» — это
 *     ответ на ОДНО намеренное нажатие над двумя сотнями строк, и человек
 *     ждёт его там, куда смотрит, а не в углу за таблицей. Обычные
 *     уведомления («сохранено») остаются в стопке — их место не меняется.
 *     Момент показа — на совести вызывающего: итог обязан идти ПОСЛЕ того,
 *     как данные на экране перечитаны (см. `planCrops.ts`), иначе «22» читается
 *     раньше, чем таблица его показывает.
 */

export type ToastKind = 'success' | 'error' | 'warning' | 'info';

/** Где карточка стоит на экране: обычная стопка снизу справа или центр. */
export type ToastPlacement = 'stack' | 'center';

export interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
  /** Висит до явного закрытия (ошибки/предупреждения). */
  sticky: boolean;
  placement: ToastPlacement;
}

export interface ToastOptions {
  placement?: ToastPlacement;
}

/** Время автоскрытия, мс. `null` = не скрывать сам. */
export const TOAST_AUTO_DISMISS_MS: Record<ToastKind, number | null> = {
  success: 4000,
  info: 5000,
  warning: null,
  error: null,
};

/**
 * Итог по центру читают, а не замечают краем глаза: в нём числа («22 · 2 407
 * га»), и четырёх секунд стопки на них мало. Ошибки и предупреждения висят
 * до закрытия, как и в стопке.
 */
export const BULK_RESULT_AUTO_DISMISS_MS: Record<ToastKind, number | null> = {
  success: 7000,
  info: 7000,
  warning: null,
  error: null,
};

export function toastAutoDismissMs(kind: ToastKind, placement: ToastPlacement): number | null {
  return placement === 'center' ? BULK_RESULT_AUTO_DISMISS_MS[kind] : TOAST_AUTO_DISMISS_MS[kind];
}

/** Больше 4 карточек на экране — уже не уведомление, а стена. */
export const TOAST_MAX_VISIBLE = 4;

let items: ToastItem[] = [];
let seq = 0;
const listeners = new Set<() => void>();

/**
 * Сообщения, пришедшие ДО монтирования `<Toaster/>`.
 *
 * Так выглядит самый неудачный момент: первый запрос бутстрапа отвалился 401 /
 * сетью, `toast.error` уже позвали, а тостера в дереве ещё нет. Раньше на этом
 * месте стоял `window.alert` — уродливо, но человек хотя бы узнавал об ошибке;
 * если просто выкинуть сообщение, экран останется молча пустым. Поэтому буфер:
 * копим и отдаём первому же подписчику (follow-up архитектора к #792, п.1).
 */
let pending: Array<{ kind: ToastKind; message: string; placement: ToastPlacement }> = [];

function notify(): void {
  for (const l of listeners) l();
}

export function subscribeToasts(listener: () => void): () => void {
  const first = listeners.size === 0;
  listeners.add(listener);

  if (first && pending.length > 0) {
    const queued = pending;
    pending = [];
    for (const { kind, message, placement } of queued) admit(kind, message, placement);
    notify();
  }

  return () => {
    listeners.delete(listener);
  };
}

/** Снимок для `useSyncExternalStore` — стабильная ссылка между изменениями. */
export function getToasts(): ToastItem[] {
  return items;
}

export function dismissToast(id: number): void {
  const next = items.filter((t) => t.id !== id);
  if (next.length === items.length) return;
  items = next;
  notify();
}

/** Для тестов и для logout — сбросить всё, включая недоставленный буфер. */
export function clearToasts(): void {
  pending = [];
  if (items.length === 0) return;
  items = [];
  notify();
}

function isSticky(kind: ToastKind): boolean {
  return TOAST_AUTO_DISMISS_MS[kind] === null;
}

/**
 * Обрезать очередь до `TOAST_MAX_VISIBLE`, жертвуя СНАЧАЛА самоскрывающимися
 * уведомлениями и никогда — только что добавленным (последним) элементом.
 * Одно правило и для экрана, и для буфера «до монтирования»: иначе ошибка,
 * пришедшая первой на бутстрапе, вытеснялась бы пачкой info ещё до показа.
 */
function trim<T extends { kind: ToastKind }>(queue: T[]): T[] {
  const next = [...queue];
  while (next.length > TOAST_MAX_VISIBLE) {
    const older = next.slice(0, -1);
    const victim = older.findIndex((t) => !isSticky(t.kind));
    next.splice(victim === -1 ? 0 : victim, 1);
  }
  return next;
}

/**
 * Положить сообщение в очередь показа. Без `notify` — вызывающий решает,
 * когда оповещать (`show` — сразу, `subscribeToasts` — один раз на весь буфер).
 */
function admit(kind: ToastKind, message: string, placement: ToastPlacement): number {
  // Тот же текст уже висит — не множим стопку.
  const dup = items.find((t) => t.kind === kind && t.message === message);
  if (dup) return dup.id;

  const item: ToastItem = { id: ++seq, kind, message, sticky: isSticky(kind), placement };

  // Переполнение: вытесняем СНАЧАЛА самоскрывающиеся (success/info) — они и
  // так уйдут через несколько секунд. Иначе пачка «сохранено» выбивала с
  // экрана непрочитанную ошибку, которую никто не закрывал (follow-up
  // архитектора к #792, п.2). Если весь стек — ошибки, жертвуем самой старой:
  // показать больше, чем TOAST_MAX_VISIBLE, всё равно нельзя.
  items = trim([...items, item]);
  return item.id;
}

function show(kind: ToastKind, msg: string, opts?: ToastOptions): number | null {
  const message = (msg ?? '').toString().trim();
  if (!message) return null;
  const placement: ToastPlacement = opts?.placement ?? 'stack';

  if (listeners.size === 0) {
    // Тостер ещё не смонтирован — не теряем сообщение молча: пишем в консоль
    // и придерживаем до первого подписчика.
    // eslint-disable-next-line no-console
    console.log(`[toast/${kind}]`, message);
    if (!pending.some((p) => p.kind === kind && p.message === message)) {
      pending = trim([...pending, { kind, message, placement }]);
    }
    return null;
  }

  const id = admit(kind, message, placement);
  notify();
  return id;
}

export const toast = {
  success: (msg: string, opts?: ToastOptions) => show('success', msg, opts),
  error: (msg: string, opts?: ToastOptions) => show('error', msg, opts),
  warning: (msg: string, opts?: ToastOptions) => show('warning', msg, opts),
  info: (msg: string, opts?: ToastOptions) => show('info', msg, opts),
};

/**
 * Итог массового действия — ОДНА карточка по центру экрана (AK-721).
 *
 * Тон (`kind`) выбирает вызывающий по факту: всё легло — success, ничего не
 * легло — warning, ничего не легло и были отказы — error. Показывать ТОЛЬКО
 * после перечитки данных, которые действие изменило.
 */
export function toastBulkResult(kind: ToastKind, message: string): number | null {
  // Через публичный `toast[kind]`, а не `show` напрямую: тесты экранов
  // подменяют `toast.error` / `toast.warning` шпионом и считают итоги — они
  // обязаны видеть и итог по центру.
  return toast[kind](message, { placement: 'center' });
}
